import {
  Stack,
  Text,
  Inline,
  Button,
  ButtonIcon,
  IconThumb,
  Divider,
  Textarea,
  IconArrow,
  Columns,
  Column,
  Notice,
} from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';

import type { CareerFeedMessage } from '../types';

type CareerFeedAssistantQuestionSuggestion = {
  label: string;
  nextPrompt: string;
  showFreeTextField?: boolean;
  isSkipButton?: boolean;
};

type CareerFeedFreeTextInputProps = {
  onSendMessage: (message: string, skip: boolean) => void;
  isLoading: boolean;
};

type CareerFeedAssistantQuestionProps = {
  message: string;
  suggestions: CareerFeedAssistantQuestionSuggestion[];
  isLoading: boolean;
  onSendMessage: (message: string, skip: boolean) => void;
  onSendFeedback: (sentiment: boolean) => void;
  showFreeTextFieldOnly?: boolean;
  hasError: boolean;
};

type CareerFeedAssistantQuestionsProps = {
  messages: CareerFeedMessage[];
  onSendMessage: (message: string, skip: boolean) => void;
  hasError: boolean;
  onSendFeedback: (sentiment: boolean) => void;
};

const TextSession = ({ text }: { text: string }) => (
  <Stack space="xsmall">
    {text.split('\n').map((line, index) => (
      <Text key={index}>{line}</Text>
    ))}
  </Stack>
);

const CHARACTER_LIMIT_FOR_FREE_TEXT = 280;

const CareerFeedFreeTextInput = ({
  onSendMessage,
  isLoading,
}: CareerFeedFreeTextInputProps) => {
  const [inputField, setInputField] = useState('');

  const onSendButtonClick = useCallback(() => {
    if (
      inputField.length > 0 &&
      !isLoading &&
      inputField.length <= CHARACTER_LIMIT_FOR_FREE_TEXT
    ) {
      onSendMessage(inputField, false);
    }
  }, [inputField, isLoading, onSendMessage]);

  return (
    <Columns space="small" alignY="center">
      <Column>
        <Textarea
          aria-label="message"
          id="career-feed-free-text-area"
          value={inputField}
          onChange={(event) => setInputField(event.currentTarget.value)}
          characterLimit={CHARACTER_LIMIT_FOR_FREE_TEXT}
          disabled={isLoading}
        />
      </Column>
      <Column width="content">
        <ButtonIcon
          icon={<IconArrow direction="up" />}
          label="Send"
          id="send-message"
          onClick={onSendButtonClick}
        />
      </Column>
    </Columns>
  );
};

const feedbackComponent = ({
  sentFeedback,
  positiveFeedbackAction,
  negativeFeedbackAction,
  selectedPositiveFeedback,
  selectedNegativeFeedback,
}: {
  sentFeedback: boolean;
  positiveFeedbackAction: () => void;
  negativeFeedbackAction: () => void;
  selectedPositiveFeedback: boolean;
  selectedNegativeFeedback: boolean;
}) => {
  if (sentFeedback) {
    return (
      <Stack space="large">
        <Notice tone="positive">
          <Text>Thanks for your feedback</Text>
        </Notice>
      </Stack>
    );
  }

  return (
    <Inline space="small" alignY="center">
      <Text>Is this helpful?</Text>

      <ButtonIcon
        icon={<IconThumb />}
        id="positive-button"
        label="Yes"
        onClick={positiveFeedbackAction}
        variant={selectedPositiveFeedback ? 'soft' : 'transparent'}
        bleed={false}
      />

      <ButtonIcon
        icon={<IconThumb direction="down" />}
        id="negative-button"
        label="No"
        onClick={negativeFeedbackAction}
        variant={selectedNegativeFeedback ? 'soft' : 'transparent'}
        bleed={false}
      />
    </Inline>
  );
};

const CareerFeedAssistantQuestion = ({
  message,
  suggestions,
  isLoading,
  onSendMessage,
  onSendFeedback,
  showFreeTextFieldOnly,
  hasError,
}: CareerFeedAssistantQuestionProps) => {
  const [showFreeTextField, setShowFreeTextField] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<{
    nextPrompt: string;
    suggestionNumber: number;
    isSkipButton?: boolean;
  } | null>(null);

  const [selectedPositiveFeedback, setSelectedPositiveFeedback] =
    useState(false);
  const [selectedNegativeFeedback, setSelectedNegativeFeedback] =
    useState(false);

  const [sentFeedback, setSentFeedback] = useState(false);

  useEffect(() => {
    setSelectedSuggestion(null);
    setShowFreeTextField(false);
    setSelectedPositiveFeedback(false);
    setSelectedNegativeFeedback(false);
    setSentFeedback(false);
  }, [message]);

  useEffect(() => {
    if (showFreeTextFieldOnly) {
      setShowFreeTextField(true);
    }
  }, [showFreeTextFieldOnly]);

  const onSuggestionClick = useCallback(
    ({
      nextPrompt,
      suggestionNumber,
      isSkipButton,
    }: {
      nextPrompt: string;
      suggestionNumber: number;
      isSkipButton?: boolean;
    }) => {
      if (!isLoading) {
        setSelectedSuggestion({ nextPrompt, suggestionNumber, isSkipButton });
      }
    },
    [isLoading],
  );

  const onNextButtonClick = useCallback(() => {
    if (selectedSuggestion) {
      onSendMessage(
        !selectedSuggestion.isSkipButton ? selectedSuggestion.nextPrompt : '',
        Boolean(selectedSuggestion.isSkipButton),
      );
    }
  }, [onSendMessage, selectedSuggestion]);

  const onSkipButtonClick = useCallback(() => {
    onSendMessage('', true);
  }, [onSendMessage]);

  return (
    <Stack space="medium">
      <TextSession text={message} />
      {showFreeTextFieldOnly ? (
        <Inline space="none">
          <Button size="small" onClick={onSkipButtonClick} variant="ghost">
            Skip
          </Button>
        </Inline>
      ) : null}
      {suggestions.length > 0 ? (
        <Inline space="xsmall">
          {suggestions.map((suggestion, index) => (
            <Button
              key={index}
              size="small"
              onClick={() => {
                if (!isLoading) {
                  setShowFreeTextField(Boolean(suggestion.showFreeTextField));
                  onSuggestionClick({
                    nextPrompt: suggestion.nextPrompt,
                    isSkipButton: suggestion.isSkipButton,
                    suggestionNumber: index,
                  });
                }
              }}
              variant={
                selectedSuggestion?.suggestionNumber === index
                  ? 'solid'
                  : 'ghost'
              }
            >
              {suggestion.label}
            </Button>
          ))}
        </Inline>
      ) : null}
      {!showFreeTextFieldOnly &&
        feedbackComponent({
          positiveFeedbackAction: () => {
            onSendFeedback(true);
            setSelectedPositiveFeedback(true);
            setSentFeedback(true);
          },
          negativeFeedbackAction: () => {
            onSendFeedback(false);
            setSelectedNegativeFeedback(true);
            setSentFeedback(true);
          },
          selectedPositiveFeedback,
          selectedNegativeFeedback,
          sentFeedback,
        })}
      {hasError ? (
        <Text tone="critical">An error occurred. Please try again</Text>
      ) : null}
      <Divider />
      {showFreeTextField ? (
        <CareerFeedFreeTextInput
          onSendMessage={onSendMessage}
          isLoading={isLoading}
        />
      ) : (
        <Button
          tone="brandAccent"
          variant="soft"
          loading={isLoading}
          onClick={onNextButtonClick}
        >
          Next
        </Button>
      )}
    </Stack>
  );
};

export const CareerFeedAssistantQuestions = ({
  messages,
  onSendMessage,
  hasError,
  onSendFeedback,
}: CareerFeedAssistantQuestionsProps) => {
  const [lastAssistantMessage] = messages
    .filter((message) => message.role === 'assistant' && !message.loading)
    .slice(-1);

  const isSendingMessage = messages
    .filter((message) => message.role === 'assistant')
    .slice(-1)?.[0].loading;

  if (
    !lastAssistantMessage ||
    lastAssistantMessage.role !== 'assistant' ||
    lastAssistantMessage.loading
  ) {
    return null;
  }

  return (
    <CareerFeedAssistantQuestion
      message={lastAssistantMessage.text}
      suggestions={lastAssistantMessage.suggestions ?? []}
      isLoading={isSendingMessage}
      onSendMessage={onSendMessage}
      showFreeTextFieldOnly={lastAssistantMessage.collectGeneralRequirements}
      hasError={hasError}
      onSendFeedback={onSendFeedback}
    />
  );
};
